/**
 * CoverageBar.tsx — Barra tricolor de cobertura (incluidos/ausentes/sobrantes)
 *
 * Resuelve inconsistencia UI-03: barra de progreso/estado ausente en P3
 *
 * UID-03b | Fase 3 | feat/step3-coteja
 * @date 2026-03-06
 */

import type { FC, CSSProperties } from 'react';
import { colors, typography, borderRadius } from '@/design-system/v2';

interface CoverageBarProps {
  incluidos: number;
  ausentes: number;
  sobrantes: number;
}

const C_INC = colors.success;        // lima   #A3E635
const C_AUS = colors.error;          // rojo   #f87171
const C_SOB = colors.info;           // azul   #3b82f6

export const CoverageBar: FC<CoverageBarProps> = ({ incluidos, ausentes, sobrantes }) => {
  const total = (incluidos + ausentes + sobrantes) || 1;
  const pImp = Math.round((incluidos / total) * 100);
  const pAus = Math.round((ausentes  / total) * 100);
  const pSob = 100 - pImp - pAus;

  const wrapStyle: CSSProperties = {
    marginBottom: '16px',
    fontFamily: typography.fontFamily.sans,
  };

  const trackStyle: CSSProperties = {
    display: 'flex',
    height: '6px',
    borderRadius: borderRadius.full,
    overflow: 'hidden',
    background: colors.bg.tertiary,
    marginBottom: '8px',
  };

  const legendStyle: CSSProperties = {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    fontSize: typography.fontSize.xs,
    color: colors.fg.secondary,
  };

  const dotStyle = (bg: string): CSSProperties => ({
    display: 'inline-block',
    width: '8px', height: '8px',
    borderRadius: borderRadius.full,
    background: bg,
    marginRight: '5px',
    flexShrink: 0,
  });

  const SEGMENTS: Array<[number, string]> = [
    [pImp, C_INC],
    [pAus, C_AUS],
    [pSob, C_SOB],
  ];

  const LEGEND = [
    { color: C_INC, count: incluidos, label: 'incluidos' },
    { color: C_AUS, count: ausentes,  label: 'ausentes'  },
    { color: C_SOB, count: sobrantes, label: 'sobrantes' },
  ];

  return (
    <div style={wrapStyle}>
      <div style={trackStyle}>
        {SEGMENTS.map(([pct, bg], i) =>
          pct > 0 ? (
            <div key={i} style={{ width: `${pct}%`, background: bg, transition: 'width 0.3s ease' }} />
          ) : null
        )}
      </div>
      <div style={legendStyle}>
        {LEGEND.map(({ color, count, label }) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center' }}>
            <span style={dotStyle(color)} />
            <span style={{ fontFamily: typography.fontFamily.mono, color }}>{count}</span>
            <span style={{ marginLeft: '3px' }}>{label}</span>
          </span>
        ))}
        <span style={{ marginLeft: 'auto', color: colors.fg.subtle }}>
          {incluidos + ausentes + sobrantes} en catálogo
        </span>
      </div>
    </div>
  );
};
